import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSpring, animated, config } from 'react-spring';
import '../styles/Hero.css';
import { LOCALES } from '../utils/localize';

const buildIntroSnippet = (name) => [
  { text: 'IntroFrame', className: 'hero-code-class' },
  { text: ' ', className: 'hero-code-plain' },
  { text: 'intro', className: 'hero-code-variable' },
  { text: '(', className: 'hero-code-operator' },
  { text: `"${name}"`, className: 'hero-code-string' },
  { text: ')', className: 'hero-code-operator' },
  { text: ';', className: 'hero-code-semicolon' },
];

const renderSnippet = [
  { text: 'renderIntro', className: 'hero-code-function' },
  { text: '(', className: 'hero-code-operator' },
  { text: 'intro', className: 'hero-code-variable' },
  { text: ');', className: 'hero-code-tail' },
];

const cppSnippet = [
  [
    { text: 'void', className: 'hero-code-type' },
    { text: ' ', className: 'hero-code-plain' },
    { text: 'renderIntro', className: 'hero-code-function' },
    { text: '(', className: 'hero-code-operator' },
    { text: 'const', className: 'hero-code-keyword' },
    { text: ' ', className: 'hero-code-plain' },
    { text: 'IntroFrame', className: 'hero-code-class' },
    { text: '& ', className: 'hero-code-operator' },
    { text: 'intro', className: 'hero-code-variable' },
    { text: ') {', className: 'hero-code-tail' },
  ],
  [
    { text: '  ', className: 'hero-code-plain' },
    { text: 'const', className: 'hero-code-keyword' },
    { text: ' ', className: 'hero-code-plain' },
    { text: 'auto', className: 'hero-code-keyword' },
    { text: ' ', className: 'hero-code-plain' },
    { text: 'sections', className: 'hero-code-variable' },
    { text: ' = ', className: 'hero-code-operator' },
    { text: 'intro', className: 'hero-code-variable' },
    { text: '.', className: 'hero-code-operator' },
    { text: 'valueMap', className: 'hero-code-function' },
    { text: '();', className: 'hero-code-tail' },
  ],
  [
    { text: '  ', className: 'hero-code-plain' },
    { text: 'for', className: 'hero-code-keyword' },
    { text: ' ', className: 'hero-code-plain' },
    { text: '(', className: 'hero-code-operator' },
    { text: 'const', className: 'hero-code-keyword' },
    { text: ' ', className: 'hero-code-plain' },
    { text: 'auto', className: 'hero-code-keyword' },
    { text: '& ', className: 'hero-code-operator' },
    { text: '[key, payload]', className: 'hero-code-variable' },
    { text: ' : ', className: 'hero-code-operator' },
    { text: 'sections', className: 'hero-code-variable' },
    { text: ') {', className: 'hero-code-tail' },
  ],
  [
    { text: '  ', className: 'hero-code-plain' },
    { text: '  ', className: 'hero-code-plain' },
    { text: 'middleware', className: 'hero-code-property' },
    { text: '.', className: 'hero-code-operator' },
    { text: 'attach', className: 'hero-code-function' },
    { text: '(', className: 'hero-code-operator' },
    { text: 'key', className: 'hero-code-variable' },
    { text: ');', className: 'hero-code-tail' },
  ],
  [
    { text: '  ', className: 'hero-code-plain' },
    { text: '  ', className: 'hero-code-plain' },
    { text: 'middleware', className: 'hero-code-property' },
    { text: '.', className: 'hero-code-operator' },
    { text: 'route', className: 'hero-code-function' },
    { text: '(', className: 'hero-code-operator' },
    { text: 'key', className: 'hero-code-variable' },
    { text: ', ', className: 'hero-code-operator' },
    { text: 'payload', className: 'hero-code-variable' },
    { text: ');', className: 'hero-code-tail' },
  ],
  [
    { text: '  ', className: 'hero-code-plain' },
    { text: '}', className: 'hero-code-tail' },
  ],
  [
    { text: '  ', className: 'hero-code-plain' },
    { text: 'middleware', className: 'hero-code-property' },
    { text: '.', className: 'hero-code-operator' },
    { text: 'flush', className: 'hero-code-function' },
    { text: '(', className: 'hero-code-operator' },
    { text: 'portfolioBus', className: 'hero-code-variable' },
    { text: ');', className: 'hero-code-tail' },
  ],
  [
    { text: '}', className: 'hero-code-tail' },
  ],
];

const getSnippetLength = (snippet) => snippet.reduce((length, token) => length + token.text.length, 0);
const getSnippetText = (snippet) => snippet.map((token) => token.text).join('');
const renderLineLength = getSnippetLength(renderSnippet);
const renderLineText = getSnippetText(renderSnippet);
const cppLineTexts = cppSnippet.map((line) => getSnippetText(line));
const cppLineLengths = cppSnippet.map((line) => (
  line.reduce((length, token) => length + token.text.length, 0)
));

const Hero = ({ locale = LOCALES.KR, onLocaleChange = () => {} }) => {
  const heroMessage = locale === LOCALES.EN ? 'HeeJun Park' : '박희준';
  const introSnippet = useMemo(() => buildIntroSnippet(heroMessage), [heroMessage]);
  const introLineLength = useMemo(() => getSnippetLength(introSnippet), [introSnippet]);
  const introLineText = useMemo(() => getSnippetText(introSnippet), [introSnippet]);
  const heroRef = useRef(null);
  const [primaryMode, setPrimaryMode] = useState('intro');
  const [typedPrimaryCount, setTypedPrimaryCount] = useState(0);
  const [primaryTypingState, setPrimaryTypingState] = useState({ key: 0, speed: 68, startDelay: 180 });
  const [windowMode, setWindowMode] = useState('default');
  const [terminalOutput, setTerminalOutput] = useState('');
  const [typedOutputCount, setTypedOutputCount] = useState(0);
  const [outputTypingState, setOutputTypingState] = useState({ key: 0, speed: 28 });
  const [isOutputVisible, setIsOutputVisible] = useState(false);
  const [isOutputHiding, setIsOutputHiding] = useState(false);
  const [outputTone, setOutputTone] = useState('neutral');
  const [terminalFx, setTerminalFx] = useState('');
  const [terminalNotice, setTerminalNotice] = useState({
    tone: 'neutral',
    text: 'PARKHEEJUN / SOFTWARE ENGINEER',
  });
  const [typedCppCounts, setTypedCppCounts] = useState(() => cppSnippet.map(() => 0));
  const [activeCppLine, setActiveCppLine] = useState(-1);
  const [cppTypingKey, setCppTypingKey] = useState(0);
  const effectTimeoutRef = useRef(null);
  const noticeTimeoutRef = useRef(null);
  const restoreTimeoutRef = useRef(null);
  const outputHideTimeoutRef = useRef(null);
  const outputTransitionTimeoutRef = useRef(null);
  const cppTypingTimeoutsRef = useRef([]);
  const scrollRenderModeRef = useRef(false);
  const primaryModeRef = useRef(primaryMode);

  useEffect(() => {
    primaryModeRef.current = primaryMode;
  }, [primaryMode]);

  const replayPrimaryTyping = (mode = 'intro', speed = 68, startDelay = 180) => {
    setPrimaryMode(mode);
    setPrimaryTypingState((currentState) => ({
      key: currentState.key + 1,
      speed,
      startDelay,
    }));
  };

  useEffect(() => {
    if (windowMode === 'expanded') {
      return;
    }

    const nextMode = scrollRenderModeRef.current ? 'render' : 'intro';
    replayPrimaryTyping(nextMode, nextMode === 'render' ? 34 : 62, 60);
  }, [heroMessage, windowMode]);

  const showTerminalOutput = (text, speed = 28, hideAfter = 1500, tone = 'neutral') => {
    window.clearTimeout(outputHideTimeoutRef.current);
    window.clearTimeout(outputTransitionTimeoutRef.current);
    setTerminalOutput(text);
    setTypedOutputCount(0);
    setIsOutputVisible(true);
    setIsOutputHiding(false);
    setOutputTone(tone);
    setOutputTypingState((currentState) => ({
      key: currentState.key + 1,
      speed,
    }));

    if (hideAfter > 0 && windowMode !== 'expanded') {
      outputHideTimeoutRef.current = window.setTimeout(() => {
        hideTerminalOutput(true);
      }, hideAfter);
    }
  };

  const hideTerminalOutput = (withMotion = true, callback) => {
    window.clearTimeout(outputHideTimeoutRef.current);
    window.clearTimeout(outputTransitionTimeoutRef.current);

    if (!isOutputVisible) {
      setIsOutputVisible(false);
      setIsOutputHiding(false);
      setTypedOutputCount(0);
      setTerminalOutput('');
      setOutputTone('neutral');
      if (callback) {
        callback();
      }
      return;
    }

    if (!withMotion) {
      setIsOutputVisible(false);
      setIsOutputHiding(false);
      setTypedOutputCount(0);
      setTerminalOutput('');
      setOutputTone('neutral');
      if (callback) {
        callback();
      }
      return;
    }

    setIsOutputHiding(true);
    outputTransitionTimeoutRef.current = window.setTimeout(() => {
      setIsOutputVisible(false);
      setIsOutputHiding(false);
      setTypedOutputCount(0);
      setTerminalOutput('');
      setOutputTone('neutral');
      if (callback) {
        callback();
      }
    }, 220);
  };

  const replaceTerminalOutput = (text, speed = 28, hideAfter = 1500, tone = 'neutral') => {
    if (isOutputVisible) {
      hideTerminalOutput(true, () => {
        showTerminalOutput(text, speed, hideAfter, tone);
      });
      return;
    }

    showTerminalOutput(text, speed, hideAfter, tone);
  };

  const pulseTerminal = (effectName, duration = 1100) => {
    window.clearTimeout(effectTimeoutRef.current);
    setTerminalFx(effectName);
    effectTimeoutRef.current = window.setTimeout(() => {
      setTerminalFx('');
    }, duration);
  };

  const clearCppTypingTimeouts = () => {
    cppTypingTimeoutsRef.current.forEach((timeoutId) => {
      window.clearTimeout(timeoutId);
    });
    cppTypingTimeoutsRef.current = [];
  };

  const getDefaultNotice = (mode = windowMode) => (
    mode === 'expanded' ? 'Maximized Workspace' : 'PARKHEEJUN / SOFTWARE ENGINEER'
  );

  const queueNotice = (text, tone, duration = 1500, nextMode = windowMode) => {
    window.clearTimeout(noticeTimeoutRef.current);
    setTerminalNotice({ tone, text });

    if (duration > 0) {
      noticeTimeoutRef.current = window.setTimeout(() => {
        setTerminalNotice({
          tone: 'neutral',
          text: getDefaultNotice(nextMode),
        });
      }, duration);
    }
  };

  const getTypingDelay = (character, baseSpeed) => {
    if (character === '.') {
      return baseSpeed + 110;
    }

    if (character === '"' || character === ';') {
      return Math.max(baseSpeed - 28, 34);
    }

    if (character === ' ') {
      return Math.max(baseSpeed - 22, 42);
    }

    return baseSpeed + Math.floor(Math.random() * 12);
  };

  const renderTypedLine = (tokens, visibleCharacters) => {
    let remainingCharacters = visibleCharacters;

    return tokens.map((token, index) => {
      if (remainingCharacters <= 0) {
        return null;
      }

      const visibleText = token.text.slice(0, remainingCharacters);
      remainingCharacters -= visibleText.length;

      if (!visibleText) {
        return null;
      }

      if (token.className === 'hero-code-tail') {
        return (
          <span key={`${token.className}-${index}`} className={token.className}>
            {token.text}
          </span>
        );
      }

      return (
        <span key={`${token.className}-${index}`} className={token.className}>
          {visibleText}
        </span>
      );
    });
  };

  useEffect(() => {
    let timeoutId;
    let characterIndex = 0;
    const activeLineLength = primaryModeRef.current === 'render' ? renderLineLength : introLineLength;
    const activeLineText = primaryModeRef.current === 'render' ? renderLineText : introLineText;

    setTypedPrimaryCount(0);

    const typeNextCharacter = () => {
      if (characterIndex >= activeLineLength) {
        return;
      }

      const currentCharacter = activeLineText[characterIndex];
      timeoutId = window.setTimeout(() => {
        characterIndex += 1;
        setTypedPrimaryCount(characterIndex);
        typeNextCharacter();
      }, getTypingDelay(currentCharacter, primaryTypingState.speed));
    };

    const startId = window.setTimeout(typeNextCharacter, primaryTypingState.startDelay);

    return () => {
      window.clearTimeout(startId);
      window.clearTimeout(timeoutId);
    };
  }, [introLineLength, introLineText, primaryTypingState]);

  useEffect(() => {
    if (!isOutputVisible || !terminalOutput) {
      setTypedOutputCount(0);
      return undefined;
    }

    let timeoutId;
    let characterIndex = 0;

    const typeNextCharacter = () => {
      if (characterIndex >= terminalOutput.length) {
        return;
      }

      const currentCharacter = terminalOutput[characterIndex];
      timeoutId = window.setTimeout(() => {
        characterIndex += 1;
        setTypedOutputCount(characterIndex);
        typeNextCharacter();
      }, getTypingDelay(currentCharacter, outputTypingState.speed));
    };

    const startId = window.setTimeout(typeNextCharacter, 70);

    return () => {
      window.clearTimeout(startId);
      window.clearTimeout(timeoutId);
    };
  }, [isOutputVisible, outputTypingState, terminalOutput]);

  useEffect(() => {
    if (windowMode !== 'expanded') {
      clearCppTypingTimeouts();
      setActiveCppLine(-1);
      setTypedCppCounts(cppSnippet.map(() => 0));
      return undefined;
    }

    clearCppTypingTimeouts();
    setActiveCppLine(-1);
    setTypedCppCounts(cppSnippet.map(() => 0));

    let lineIndex = 0;
    let characterIndex = 0;

    const typeCppCharacter = () => {
      if (lineIndex >= cppSnippet.length) {
        setActiveCppLine(-1);
        return;
      }

      const currentLineIndex = lineIndex;
      const currentLineLength = cppLineLengths[currentLineIndex];
      const currentLineText = cppLineTexts[currentLineIndex];

      if (characterIndex === 0) {
        setActiveCppLine(currentLineIndex);
      }

      const timeoutId = window.setTimeout(() => {
        const nextCharacterIndex = characterIndex + 1;
        setTypedCppCounts((currentLines) => {
          const nextLines = [...currentLines];
          nextLines[currentLineIndex] = nextCharacterIndex;
          return nextLines;
        });

        characterIndex = nextCharacterIndex;

        if (nextCharacterIndex < currentLineLength) {
          typeCppCharacter();
          return;
        }

        lineIndex += 1;
        characterIndex = 0;

        if (lineIndex < cppSnippet.length) {
          const linePauseId = window.setTimeout(typeCppCharacter, 40);
          cppTypingTimeoutsRef.current.push(linePauseId);
        } else {
          setActiveCppLine(-1);
        }
      }, getTypingDelay(currentLineText[characterIndex], 60));

      cppTypingTimeoutsRef.current.push(timeoutId);
    };

    const startId = window.setTimeout(typeCppCharacter, 120);
    cppTypingTimeoutsRef.current.push(startId);

    return () => {
      clearCppTypingTimeouts();
    };
  }, [cppTypingKey, windowMode]);

  useEffect(() => {
    return () => {
      window.clearTimeout(effectTimeoutRef.current);
      window.clearTimeout(noticeTimeoutRef.current);
      window.clearTimeout(restoreTimeoutRef.current);
      window.clearTimeout(outputHideTimeoutRef.current);
      window.clearTimeout(outputTransitionTimeoutRef.current);
      clearCppTypingTimeouts();
    };
  }, []);

  useEffect(() => {
    if (windowMode === 'expanded') {
      return undefined;
    }

    const handleScrollMode = () => {
      const heroElement = heroRef.current;

      if (!heroElement) {
        return;
      }

      const rect = heroElement.getBoundingClientRect();
      const scrollOffset = Math.max(-rect.top, 0);
      const nextMode = scrollOffset > 0.5;

      if (nextMode === scrollRenderModeRef.current) {
        return;
      }

      scrollRenderModeRef.current = nextMode;
      replayPrimaryTyping(nextMode ? 'render' : 'intro', nextMode ? 34 : 58, nextMode ? 28 : 60);
    };

    handleScrollMode();
    window.addEventListener('scroll', handleScrollMode, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScrollMode);
    };
  }, [windowMode]);

  const titleSpring = useSpring({
    from: { opacity: 0, transform: 'translateY(-50px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    delay: 100,
    config: config.gentle,
  });

  const codeSpring = useSpring({
    from: { opacity: 0, transform: 'translate3d(32px, 24px, 0)' },
    to: { opacity: 1, transform: 'translate3d(0px, 0px, 0)' },
    delay: 420,
    config: config.slow,
  });

  const scrollToSkills = () => {
    document.getElementById('skills')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCloseAttempt = () => {
    window.clearTimeout(restoreTimeoutRef.current);
    replaceTerminalOutput('throw new Error("close denied");', 30, windowMode === 'expanded' ? 0 : 1700, 'error');
    queueNotice('Close Denied', 'error', 1700);
    pulseTerminal('close-error', 980);
  };

  const handleMinimize = () => {
    window.clearTimeout(restoreTimeoutRef.current);
    replaceTerminalOutput('window.minimize();', 28, windowMode === 'expanded' ? 0 : 1420, 'warning');
    queueNotice('Window Minimized', 'warning', 1420, windowMode);
    pulseTerminal('minimize', 1220);
  };

  const handleMaximizeToggle = () => {
    window.clearTimeout(restoreTimeoutRef.current);

    if (windowMode === 'expanded') {
      hideTerminalOutput(true);
      setWindowMode('default');
      queueNotice('Restored Workspace', 'success', 1450, 'default');
      pulseTerminal('restore', 860);
      const nextPrimaryMode = scrollRenderModeRef.current ? 'render' : 'intro';
      restoreTimeoutRef.current = window.setTimeout(() => {
        replayPrimaryTyping(nextPrimaryMode, nextPrimaryMode === 'render' ? 34 : 62, nextPrimaryMode === 'render' ? 28 : 70);
      }, 180);
      return;
    }

    clearCppTypingTimeouts();
    setActiveCppLine(-1);
    setTypedCppCounts(cppSnippet.map(() => 0));
    setPrimaryMode('intro');
    setTypedPrimaryCount(introLineLength);
    setWindowMode('expanded');
    showTerminalOutput('workspace.maximize();', 28, 0, 'success');
    queueNotice('Maximized Workspace', 'success', 1800, 'expanded');
    pulseTerminal('expand', 980);
    restoreTimeoutRef.current = window.setTimeout(() => {
      setCppTypingKey((currentKey) => currentKey + 1);
    }, 280);
  };

  const handleLocaleSelect = (nextLocale) => {
    const localeLabel = nextLocale === LOCALES.EN ? 'EN' : 'KR';

    onLocaleChange(nextLocale);
    replaceTerminalOutput(`translateIntro(Locale::${localeLabel});`, 24, windowMode === 'expanded' ? 0 : 1700, 'success');
    queueNotice(`Locale::${localeLabel}`, 'success', 1500, windowMode);
    pulseTerminal('translate', 920);
  };

  const showRoleRow = windowMode === 'expanded';
  const introLineNumber = showRoleRow ? '02' : '01';
  const outputLineNumber = showRoleRow ? '03' : '02';
  const activePrimarySnippet = primaryMode === 'render' ? renderSnippet : introSnippet;
  const primaryAriaLabel = primaryMode === 'render' ? 'renderIntro(intro);' : heroMessage;
  const showPrimaryCursor = windowMode !== 'expanded' && !isOutputVisible;
  const reservePrimaryCursor = windowMode !== 'expanded';
  const isIntroBooting = windowMode === 'default' && primaryMode === 'intro' && typedPrimaryCount === 0 && !isOutputVisible;
  const terminalClassName = [
    'hero-terminal',
    windowMode === 'expanded' ? 'is-expanded' : '',
    isIntroBooting ? 'is-intro-booting' : '',
    terminalFx ? `is-${terminalFx}` : '',
  ].filter(Boolean).join(' ');
  const terminalBodyClassName = [
    'hero-terminal-body',
    windowMode === 'default' ? 'is-default-stage' : '',
  ].filter(Boolean).join(' ');
  const heroCopyClassName = [
    'hero-copy',
    windowMode === 'expanded' ? 'is-terminal-focus' : '',
  ].filter(Boolean).join(' ');
  const heroPrimaryClassName = [
    'hero-primary',
    windowMode === 'expanded' ? 'is-expanded' : '',
  ].filter(Boolean).join(' ');
  const visibleCppLines = cppSnippet.filter((line, index) => (
    typedCppCounts[index] > 0 || activeCppLine === index
  ));

  useEffect(() => {
    const handlePortfolioReload = () => {
      window.clearTimeout(effectTimeoutRef.current);
      window.clearTimeout(noticeTimeoutRef.current);
      window.clearTimeout(restoreTimeoutRef.current);
      window.clearTimeout(outputHideTimeoutRef.current);
      clearCppTypingTimeouts();
      scrollRenderModeRef.current = false;
      setWindowMode('default');
      setTerminalFx('');
      setTerminalOutput('');
      setTypedOutputCount(0);
      setIsOutputVisible(false);
      setIsOutputHiding(false);
      setOutputTone('neutral');
      setTerminalNotice({ tone: 'neutral', text: 'PARKHEEJUN / SOFTWARE ENGINEER' });
      setActiveCppLine(-1);
      setTypedCppCounts(cppSnippet.map(() => 0));
      setPrimaryMode('intro');
      setTypedPrimaryCount(0);

      window.setTimeout(() => {
        replayPrimaryTyping('intro', 66, 120);
      }, 120);
    };

    window.addEventListener('portfolio:reload', handlePortfolioReload);

    return () => {
      window.removeEventListener('portfolio:reload', handlePortfolioReload);
    };
  }, []);

  return (
    <section className="hero" id="home" ref={heroRef}>
      <div className="hero-orb hero-orb-one" aria-hidden="true" />
      <div className="hero-orb hero-orb-two" aria-hidden="true" />
      <div className="section-shell hero-shell">
        <animated.pre style={codeSpring} className="hero-code-bg" aria-hidden="true">{`int main(void) {
  boot_linux_runtime();
  init_device_bus(UART, I2C, GPIO);
  sync_embedded_state();
  run_product_experience();
}`}</animated.pre>
        <div className={heroCopyClassName}>
          <div className={heroPrimaryClassName}>
            <div className="hero-terminal-stage">
              <div className="hero-terminal-locale" role="group" aria-label="Language selector">
                <button
                  type="button"
                  className={`hero-terminal-locale-button ${locale === LOCALES.EN ? 'is-active' : ''}`}
                  onClick={() => handleLocaleSelect(LOCALES.EN)}
                  aria-pressed={locale === LOCALES.EN}
                >
                  EN
                </button>
                <span aria-hidden="true">/</span>
                <button
                  type="button"
                  className={`hero-terminal-locale-button ${locale === LOCALES.KR ? 'is-active' : ''}`}
                  onClick={() => handleLocaleSelect(LOCALES.KR)}
                  aria-pressed={locale === LOCALES.KR}
                >
                  KR
                </button>
              </div>
              <animated.div style={titleSpring} className={terminalClassName}>
              <div className="hero-terminal-bar">
                <button type="button" className="hero-terminal-control is-red" aria-label="Try closing window" onClick={handleCloseAttempt} />
                <button type="button" className="hero-terminal-control is-yellow" aria-label="Minimize window" onClick={handleMinimize} />
                <button type="button" className="hero-terminal-control is-green" aria-label={windowMode === 'expanded' ? 'Restore window' : 'Maximize window'} onClick={handleMaximizeToggle} />
                <span className="hero-terminal-label">portfolio.cpp</span>
                <span className={`hero-terminal-note is-${terminalNotice.tone}`}>{terminalNotice.text}</span>
              </div>
              <div className={terminalBodyClassName}>
                {showRoleRow && (
                  <div className="hero-code-row hero-code-row-main hero-code-row-muted">
                    <span className="hero-line-number">01</span>
                    <p className="hero-code-line">
                      <span className="hero-code-comment">{'// role: software engineer'}</span>
                    </p>
                  </div>
                )}
                <div className="hero-code-row hero-code-row-main hero-code-row-intro">
                  <span className="hero-line-number">{introLineNumber}</span>
                  <h1 className="hero-code-line" aria-label={primaryAriaLabel}>
                    {renderTypedLine(activePrimarySnippet, typedPrimaryCount)}
                    {reservePrimaryCursor && (
                      <span
                        className={`hero-code-cursor ${showPrimaryCursor ? '' : 'is-hidden'}`.trim()}
                        aria-hidden="true"
                      />
                    )}
                  </h1>
                </div>
                {isOutputVisible && (
                  <div className={`hero-code-row hero-code-row-main hero-code-row-output ${isOutputHiding ? 'is-hiding' : ''}`}>
                    <span className="hero-line-number">{outputLineNumber}</span>
                    <p className={`hero-code-line hero-code-line-output is-${outputTone}`}>
                      <span className={`hero-code-output is-${outputTone}`}>{terminalOutput.slice(0, typedOutputCount)}</span>
                      {typedOutputCount < terminalOutput.length && (
                        <span className="hero-code-cursor" aria-hidden="true" />
                      )}
                    </p>
                  </div>
                )}
                {windowMode === 'expanded' && (
                  <div className="hero-terminal-expanded" aria-live="polite">
                    {visibleCppLines.map((line, index) => {
                      const sourceIndex = cppSnippet.indexOf(line);

                      return (
                        <div className="hero-code-row hero-code-row-cpp" key={`cpp-line-${sourceIndex + 1}`}>
                          <span className="hero-line-number">{String(sourceIndex + 4).padStart(2, '0')}</span>
                          <p className="hero-code-line hero-code-line-cpp">
                            {renderTypedLine(line, typedCppCounts[sourceIndex])}
                            {activeCppLine === sourceIndex && <span className="hero-code-cursor" aria-hidden="true" />}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              </animated.div>
            </div>
          </div>
          {windowMode !== 'expanded' && (
            <div className="hero-secondary">
              <div className="hero-scroll-stage">
                <button type="button" className="hero-scroll-indicator" onClick={scrollToSkills} aria-label="Scroll to skills">
                  <span className="hero-scroll-label">Scroll</span>
                  <span className="hero-scroll-mouse" aria-hidden="true">
                    <span className="hero-scroll-wheel" />
                  </span>
                  <span className="hero-scroll-line" aria-hidden="true" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default Hero;
